/**
 * DAL: Director Calendar Data
 * Fetches upcoming shootings for a director to display in the staff calendar.
 */

import { supabase } from "@/integrations/supabase/client";

export interface DirectorCalendarShooting {
  id: string;
  date: string | null;
  status: string | null;
  title: string | null;
  plan: string | null;
  contentCount: number;
  directorName?: string | null;
  customer: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

/**
 * Get upcoming shootings for a director (via bookings table)
 */
export async function getDirectorCalendarShootings(
  userId: string
): Promise<{ success: boolean; data: DirectorCalendarShooting[] }> {
  try {
    // Get the director's team ID
    const { data: directorEntry, error: dirError } = await supabase
      .from('team_film_directors')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (dirError || !directorEntry) {
      return { success: true, data: [] };
    }

    // Find customers assigned to this director via users.director_id
    const { data: customers, error: custError } = await supabase
      .from('users')
      .select('id, first_name, last_name, avatar_url')
      .eq('director_id', directorEntry.id);

    if (custError || !customers?.length) {
      return { success: true, data: [] };
    }

    const customerIds = customers.map(c => c.id);
    const customerMap = new Map(customers.map(c => [c.id, c]));

    // Get shootings for these customers
    const { data: shootings, error: shootError } = await supabase
      .from('shootings')
      .select('id, date, status, title, plan, user_id')
      .in('user_id', customerIds);

    if (shootError || !shootings?.length) {
      return { success: true, data: [] };
    }

    const shootingIds = shootings.map(s => s.id);

    // Get booking dates as fallback when shooting.date is null
    const { data: bookings } = await supabase
      .from('bookings')
      .select('shooting_id, start_time')
      .in('shooting_id', shootingIds);

    const bookingDateMap = new Map<string, string>();
    bookings?.forEach(b => {
      if (b.shooting_id && b.start_time) {
        bookingDateMap.set(b.shooting_id, b.start_time);
      }
    });

    // Get content count per shooting
    const { data: contentCounts } = await supabase
      .from('video_content')
      .select('shooting_id')
      .in('shooting_id', shootingIds);

    const countByShootingId = new Map<string, number>();
    contentCounts?.forEach(c => {
      if (c.shooting_id) {
        countByShootingId.set(c.shooting_id, (countByShootingId.get(c.shooting_id) || 0) + 1);
      }
    });

    const result: DirectorCalendarShooting[] = shootings.map(s => {
      const cust = customerMap.get(s.user_id)!;
      const effectiveDate = s.date || bookingDateMap.get(s.id) || null;
      return {
        id: s.id,
        date: effectiveDate,
        status: s.status,
        title: s.title,
        plan: s.plan,
        contentCount: countByShootingId.get(s.id) || 0,
        customer: {
          id: cust.id,
          first_name: cust.first_name,
          last_name: cust.last_name,
          avatar_url: cust.avatar_url,
        },
      };
    });

    result.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    return { success: true, data: result };
  } catch (error) {
    console.error('Error fetching director calendar shootings:', error);
    return { success: true, data: [] };
  }
}

/**
 * Get ALL shootings across all directors (for directeur_manager role)
 * Uses users.director_id to find customer→director assignments
 */
export async function getAllDirectorCalendarShootings(): Promise<{
  success: boolean;
  data: DirectorCalendarShooting[];
}> {
  try {
    // Get all directors with their names
    const { data: directors, error: dirError } = await supabase
      .from('team_film_directors')
      .select('id, users!team_film_directors_user_id_fkey(first_name, last_name)');

    if (dirError) throw dirError;

    const directorNameMap = new Map<string, string>();
    directors?.forEach(d => {
      const u = d.users as unknown as { first_name: string; last_name: string } | null;
      directorNameMap.set(d.id, `${u?.first_name || ''} ${u?.last_name || ''}`.trim());
    });

    const directorIds = directors?.map(d => d.id) || [];
    if (directorIds.length === 0) return { success: true, data: [] };

    // Find all customers assigned to any director
    const { data: customers, error: custError } = await supabase
      .from('users')
      .select('id, first_name, last_name, avatar_url, director_id')
      .in('director_id', directorIds);

    if (custError || !customers?.length) return { success: true, data: [] };

    const customerIds = customers.map(c => c.id);
    const customerMap = new Map(customers.map(c => [c.id, c]));

    // Get all shootings for these customers
    const { data: shootings, error: shootError } = await supabase
      .from('shootings')
      .select('id, date, status, title, plan, user_id')
      .in('user_id', customerIds);

    if (shootError || !shootings?.length) return { success: true, data: [] };

    const shootingIds = shootings.map(s => s.id);

    // Get booking dates as fallback when shooting.date is null
    const { data: bookings } = await supabase
      .from('bookings')
      .select('shooting_id, start_time')
      .in('shooting_id', shootingIds);

    const bookingDateMap = new Map<string, string>();
    bookings?.forEach(b => {
      if (b.shooting_id && b.start_time) {
        bookingDateMap.set(b.shooting_id, b.start_time);
      }
    });

    const { data: contentCounts } = await supabase
      .from('video_content')
      .select('shooting_id')
      .in('shooting_id', shootingIds);

    const countByShootingId = new Map<string, number>();
    contentCounts?.forEach(c => {
      if (c.shooting_id) {
        countByShootingId.set(c.shooting_id, (countByShootingId.get(c.shooting_id) || 0) + 1);
      }
    });

    const result: DirectorCalendarShooting[] = shootings.map(s => {
      const cust = customerMap.get(s.user_id)!;
      const effectiveDate = s.date || bookingDateMap.get(s.id) || null;
      return {
        id: s.id,
        date: effectiveDate,
        status: s.status,
        title: s.title,
        plan: s.plan,
        contentCount: countByShootingId.get(s.id) || 0,
        directorName: directorNameMap.get(cust.director_id || '') || null,
        customer: {
          id: cust.id,
          first_name: cust.first_name,
          last_name: cust.last_name,
          avatar_url: cust.avatar_url,
        },
      };
    });

    result.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    return { success: true, data: result };
  } catch (error) {
    console.error('Error fetching all director calendar shootings:', error);
    return { success: true, data: [] };
  }
}
